/**
 * Custom hook for fetching and managing promotion data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPromotions, cachePromotions, getCachedPromotions } from '../services/promotions.service';

/**
 * Hook for fetching promotions with auto-refresh and offline support
 * @param {string} clientId - Client identifier
 * @param {string|number} storeId - Store/machine identifier
 * @param {object} options - Configuration options
 * @returns {object} Hook state and controls
 */
export const usePromotions = (clientId, storeId, options = {}) => {
  const {
    refreshInterval = 30000, // 30 seconds
    enableAutoRefresh = true,
    enableOfflineCache = true,
    preview = false
  } = options;

  // State management
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Refs for cleanup
  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Fetches promotions from API with error handling and caching
   */
  const fetchPromotions = useCallback(async (showLoading = true) => {
    // Don't fetch if required params are missing
    if (!clientId || !storeId) {
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      const fetchedPromotions = await getPromotions(clientId, storeId, preview);
      
      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      setPromotions(fetchedPromotions);
      setLastFetch(new Date());
      
      // Cache for offline access
      if (enableOfflineCache) {
        cachePromotions(storeId, fetchedPromotions);
      }

    } catch (err) {
      // Don't set error if request was aborted
      if (err.name === 'AbortError') {
        return;
      }

      setError(err.message || 'Failed to load promotions');

      // Try to load cached promotions if online fetch fails
      if (enableOfflineCache) {
        const cached = getCachedPromotions(storeId);
        if (cached && cached.length > 0) {
          setPromotions(cached);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, storeId, enableOfflineCache, preview]);

  /**
   * Handles online/offline status changes
   */
  const handleOnlineStatusChange = useCallback(() => {
    const online = navigator.onLine;
    setIsOnline(online);
    
    
    // Refresh data when coming back online
    if (online && !loading) {
      fetchPromotions(false);
    }
  }, [fetchPromotions, loading]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(() => {
    fetchPromotions(true);
  }, [fetchPromotions]);

  /**
   * Setup auto-refresh interval
   */
  const setupAutoRefresh = useCallback(() => {
    if (!enableAutoRefresh || intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(() => {
      // Only auto-refresh if page is visible and online
      if (document.visibilityState === 'visible' && navigator.onLine && !loading) {
        fetchPromotions(false);
      }
    }, refreshInterval);

  }, [enableAutoRefresh, refreshInterval, fetchPromotions, loading]);

  /**
   * Cleanup auto-refresh interval
   */
  const clearAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Initial fetch on mount or parameter change
  useEffect(() => {
    // Load cached data immediately for better UX
    if (enableOfflineCache && storeId) {
      const cached = getCachedPromotions(storeId);
      if (cached && cached.length > 0) {
        setPromotions(cached);
        setLoading(false);
      }
    }

    // Then fetch fresh data
    fetchPromotions(true);
  }, [fetchPromotions, enableOfflineCache, storeId]);

  // Setup auto-refresh
  useEffect(() => {
    setupAutoRefresh();
    return clearAutoRefresh;
  }, [setupAutoRefresh, clearAutoRefresh]);

  // Listen for online/offline events
  useEffect(() => {
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [handleOnlineStatusChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAutoRefresh();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [clearAutoRefresh]);

  // Pause auto-refresh when page is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearAutoRefresh();
      } else if (document.visibilityState === 'visible' && enableAutoRefresh) {
        setupAutoRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [setupAutoRefresh, clearAutoRefresh, enableAutoRefresh]);

  return {
    // Data
    promotions,
    loading,
    error,
    lastFetch,
    isOnline,
    
    // Actions
    refresh,
    
    // Computed values
    hasPromotions: promotions.length > 0,
    promotionCount: promotions.length,
    
    // Status indicators
    isEmpty: !loading && promotions.length === 0,
    hasError: !!error,
    isStale: lastFetch && Date.now() - lastFetch.getTime() > refreshInterval * 2
  };
};